import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../../lib/tokens';
import { fetchUserTutorMemory, type TutorMemoryData } from './api';
import { formatRelative } from './format';

interface Props {
  userId: string;
  app: string;
  enabled: boolean;
}

// Read-only Einsicht in das Tutor-Memory eines Users — „was Anna/Jan wissen"
// (Spec tutor-text-coverage.md C5). Lazy + collapsible; der Backend-Read hat
// KEINEN Backfill-Seiteneffekt. Memory ist personenbezogen → ADMIN-only.

const PHASE_LABELS: Record<string, string> = {
  aufbau: 'Aufbauphase',
  vertiefung: 'Vertiefungsphase',
  endspurt: 'Endspurt',
  review: 'Review',
};

const VERDICT_LABELS: Record<string, string> = {
  solid: 'sicher',
  shaky: 'wackelig',
  unfinished: 'offen',
};

export function TutorMemorySection({ userId, app, enabled }: Props): JSX.Element {
  const [open, setOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-tutor-memory', userId, app],
    queryFn: () => fetchUserTutorMemory(userId, app),
    enabled,
    staleTime: 60_000,
  });

  const memory = data?.memory ?? null;
  const meta = data?.meta ?? null;

  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline justify-between gap-3 text-left"
      >
        <h3 className="text-sm font-semibold text-text-primary">
          Memory — was der KI-Dozent über den User weiß
        </h3>
        <span className="shrink-0 text-[11px] text-text-tertiary">
          {isLoading
            ? 'lädt…'
            : memory
              ? `v${meta?.version ?? '?'} · ${meta ? formatRelative(meta.lastUpdatedAt) : ''}`
            : 'leer'}{' '}
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div className="mt-3">
          {error ? (
            <p className="text-xs" style={{ color: darkTheme.colors.danger }}>
              Fehler beim Laden: {error instanceof Error ? error.message : 'unbekannt'}
            </p>
          ) : !memory ? (
            <p className="text-xs text-text-tertiary">
              Noch kein Memory vorhanden — entsteht mit der ersten Tutor-Nutzung.
            </p>
          ) : (
            <MemoryBody memory={memory} />
          )}
        </div>
      )}
    </div>
  );
}

function MemoryBody({ memory }: { memory: TutorMemoryData }): JSX.Element {
  const { profile, learningState, notes } = memory;
  const domains = Object.entries(profile.domainKnowledge ?? {});
  const pinnedFirst = [...notes].sort(
    (a, b) => Number(b.pinned) - Number(a.pinned) || b.importance - a.importance,
  );

  return (
    <div className="space-y-3">
      {/* Grundlagen */}
      <Sub title="Profil & Präferenzen">
        <div className="flex flex-wrap gap-2">
          <Chip tone="accent">
            {PHASE_LABELS[learningState.phase] ?? learningState.phase}
          </Chip>
          <Chip>Antwortlänge: {profile.preferences.answerLength}</Chip>
          <Chip>Ton: {profile.preferences.tone}</Chip>
          {domains.map(([domain, level]) => (
            <Chip key={domain}>
              {domain}: {level}
            </Chip>
          ))}
        </div>
        {(profile.context.length > 0 || profile.backgroundNotes.length > 0) && (
          <ul className="mt-2 space-y-0.5">
            {[...profile.context, ...profile.backgroundNotes].map((line, i) => (
              <li key={i} className="text-xs text-text-secondary">
                · {line}
              </li>
            ))}
          </ul>
        )}
      </Sub>

      {/* Fehlvorstellungen */}
      <Sub title={`Hartnäckige Fehlvorstellungen (${learningState.recurringMisconceptions.length})`}>
        {learningState.recurringMisconceptions.length === 0 ? (
          <Empty>Keine erfasst.</Empty>
        ) : (
          <ul className="space-y-1.5">
            {learningState.recurringMisconceptions.map((m, i) => (
              <li key={m.id ?? i} className="text-xs">
                <span className="font-semibold text-text-primary">{m.topic}:</span>{' '}
                <span style={{ color: darkTheme.colors.danger }}>{m.misconception}</span>{' '}
                <span className="text-text-tertiary">→</span>{' '}
                <span style={{ color: darkTheme.colors.success }}>{m.correction}</span>{' '}
                <span className="text-text-tertiary">({m.observedCount}×)</span>
              </li>
            ))}
          </ul>
        )}
      </Sub>

      {/* Zuletzt besprochene Themen + offene Fäden */}
      <Sub title="Zuletzt besprochene Themen">
        {learningState.recentTopics.length === 0 ? (
          <Empty>Keine erfasst.</Empty>
        ) : (
          <div className="flex flex-wrap gap-2">
            {learningState.recentTopics.map((t, i) => (
              <Chip key={i} tone={t.verdict === 'solid' ? 'success' : undefined}>
                {t.topicTitle ?? t.topicSlug} · {VERDICT_LABELS[t.verdict] ?? t.verdict}
              </Chip>
            ))}
          </div>
        )}
        {learningState.openThreads.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {learningState.openThreads.map((t, i) => (
              <li key={i} className="text-xs text-text-secondary">
                ⤷ {t.note}
              </li>
            ))}
          </ul>
        )}
      </Sub>

      {/* Notizen */}
      <Sub title={`Notizen (${notes.length})`}>
        {notes.length === 0 ? (
          <Empty>Keine Notizen.</Empty>
        ) : (
          <ul className="space-y-1">
            {pinnedFirst.map((n) => (
              <li key={n.id} className="text-xs text-text-secondary">
                {n.pinned ? '📌 ' : '· '}
                {n.text}
              </li>
            ))}
          </ul>
        )}
        {learningState.recentDrills.length > 0 && (
          <p className="mt-2 text-[11px] text-text-tertiary">
            + {learningState.recentDrills.length} gemerkte Drill-Fragen (Wiederholungs-Vermeidung)
          </p>
        )}
      </Sub>
    </div>
  );
}

function Sub({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        {title}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-xs text-text-tertiary">{children}</p>;
}

function Chip({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: 'accent' | 'success';
}): JSX.Element {
  return (
    <span
      className="inline-flex items-baseline rounded-full px-2.5 py-1 text-[11px]"
      style={{
        background:
          tone === 'success'
            ? darkTheme.colors.successBg
            : tone === 'accent'
              ? darkTheme.colors.accentSubtle
              : darkTheme.colors.bgBase,
        color:
          tone === 'success'
            ? darkTheme.colors.success
            : tone === 'accent'
              ? darkTheme.colors.accentLight
              : darkTheme.colors.textSecondary,
      }}
    >
      {children}
    </span>
  );
}
